<template>
  <PVForm
    v-slot="$form"
    class="project-form"
    :initial-values="initialValues"
    :resolver="resolver"
    @submit="onFormSubmit"
  >
    <div class="project-form-row">
      <div class="form-field project-number">
        <label for="number">{{ $t('projectForm.number') }} *</label>
        <PVInputText
          :disabled="isEditMode || isFormReadonly"
          fluid
          name="number"
        ></PVInputText>
        <PVMessage
          v-if="$form.number?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.number.error.message }}
        </PVMessage>
      </div>
      <div v-if="isSupplierProject" class="form-field external-id">
        <label for="external-id">{{ $t('projectForm.externalId') }}</label>
        <PVInputText
          :disabled="isFormReadonly"
          fluid
          name="externalId"
        ></PVInputText>
        <PVMessage
          v-if="$form.externalId?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.externalId.error.message }}
        </PVMessage>
      </div>
      <div class="form-field">
        <label for="title">{{ $t('projectForm.title') }} *</label>
        <PVInputText
          :disabled="isFormReadonly"
          fluid
          name="title"
        ></PVInputText>
        <PVMessage
          v-if="$form.title?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.title.error.message }}
        </PVMessage>
      </div>
    </div>
    <div class="project-form-row">
      <div class="form-field">
        <label for="location">{{ $t('projectForm.location') }} *</label>
        <PVSelect
          fluid
          :disabled="isEditMode || isFormReadonly"
          :loading="isLoadingLocations"
          name="location"
          option-label="text"
          :options="locations"
          option-value="value"
        ></PVSelect>
        <PVMessage
          v-if="$form.location?.invalid"
          fluid
          severity="error"
          variant="simple"
        >
          {{ $form.location.error.message }}
        </PVMessage>
      </div>
      <div class="form-field status">
        <label for="status">{{ $t('projectForm.status') }} *</label>
        <PVSelect
          fluid
          name="status"
          :disabled="isFormReadonly"
          :option-label="
            (value: ProjectStatus) => t(`projectForm.projectStatus.${value}`)
          "
          :options="Object.values(ProjectStatus)"
        ></PVSelect>
        <PVMessage
          v-if="$form.status?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.status.error.message }}
        </PVMessage>
      </div>
      <div class="form-field">
        <label for="duration">{{ $t('projectForm.duration') }}</label>
        <PVDatePicker
          :disabled="isFormReadonly"
          fluid
          name="duration"
          selection-mode="range"
        />
        <PVMessage
          v-if="$form.duration?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.duration.error.message }}
        </PVMessage>
      </div>
      <div v-if="isSupplierProject" class="form-field">
        <label for="inspectionDueTime">
          {{ $t('projectForm.inspectionDueTime') }} *
        </label>
        <PVInputNumber
          :disabled="isFormReadonly"
          fluid
          name="inspectionDueTime"
        />
        <PVMessage
          v-if="$form.inspectionDueTime?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.inspectionDueTime.error.message }}
        </PVMessage>
      </div>
    </div>
    <div class="project-form-row">
      <div class="form-field">
        <label for="description">{{ $t('projectForm.description') }}</label>
        <PVTextarea
          :disabled="isFormReadonly"
          fluid
          name="description"
          style="resize: none"
        />
        <PVMessage
          v-if="$form.description?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.description.error.message }}
        </PVMessage>
      </div>
    </div>

    <slot></slot>

    <div class="buttons-container">
      <vf-button
        v-if="!isFormReadonly && displayCancelButton && isFormDirty($form)"
        :size="buttonSizes.large"
        :variant="ButtonVariant.PRIMARY"
        @click="$form.reset()"
        >{{ t('projectForm.cancel') }}</vf-button
      >
      <vf-button
        v-if="!isFormReadonly && isFormDirty($form)"
        :size="buttonSizes.large"
        type="submit"
        :variant="ButtonVariant.SECONDARY"
      >
        {{
          isEditMode ? t('projectForm.saveChanges') : t('projectForm.submit')
        }}
      </vf-button>
    </div>
  </PVForm>
</template>

<script lang="ts" setup>
  import { reactive, ref } from 'vue';
  import {
    ProjectFormFields,
    ProjectFormMode,
    projectFormValidator,
  } from './ProjectForm.types';
  import { ProjectStatus, ProjectType, Role } from 'types';
  import { FormFieldState, FormSubmitEvent } from '@primevue/forms/form';
  import { zodResolver } from '@primevue/forms/resolvers/zod';
  import { useFetch } from '@/composables/useFetch';
  import { useRoles } from '@/composables/useRoles';
  import { ProjectDataService } from '@/services';
  import { useI18n } from 'vue-i18n';
  import PVDatePicker from 'primevue/datepicker';
  import PVInputText from 'primevue/inputtext';
  import PVSelect from 'primevue/select';
  import PVInputNumber from 'primevue/inputnumber';
  import { ButtonVariant } from '../VfButton/VfButton.types';
  import { computed } from 'vue';

  const { t } = useI18n();
  const { isUserRole } = useRoles();

  const {
    displayCancelButton = false,
    mode = ProjectFormMode.CREATE,
    projectData,
    projectType,
  } = defineProps<{
    displayCancelButton?: boolean;
    mode?: ProjectFormMode;
    projectData?: ProjectFormFields;
    projectType: ProjectType;
  }>();
  const emit = defineEmits<{
    (e: 'submit', formData: ProjectFormFields): void;
  }>();

  const initialValues = reactive<ProjectFormFields>(
    projectData
      ? projectData
      : {
          title: '',
          number: '',
          externalId: '',
          location: '',
          status: ProjectStatus.ACTIVE,
          inspectionDueTime: 14,
          description: '',
          duration: [],
        },
  );
  const resolver = ref(zodResolver(projectFormValidator(projectType)));

  const { data: locations = [], isLoading: isLoadingLocations } = useFetch(
    ProjectDataService.getPossibleProjectLocationsForSelect,
  );

  const isEditMode = computed(() => mode === ProjectFormMode.EDIT);
  const isSupplierProject = computed(
    () => projectType === ProjectType.SUPPLIER_PROJECT,
  );
  const isFormReadonly = computed(() => {
    const editProjectAllowedRoles = [Role.Admin];
    if (projectType === ProjectType.SUPPLIER_PROJECT) {
      editProjectAllowedRoles.push(Role.DocuManager);
    } else if (projectType === ProjectType.DIGITALIZATION_PROJECT) {
      editProjectAllowedRoles.push(Role.DocuResponsible);
    }
    return !isUserRole(editProjectAllowedRoles);
  });

  function isFormDirty(form: Record<string, FormFieldState>) {
    return Object.values(form).some((field) => field.dirty);
  }

  function onFormSubmit(form: FormSubmitEvent) {
    if (!form.valid) return;
    emit('submit', form.values as ProjectFormFields);
  }
</script>

<style lang="scss" scoped>
  .project-form {
    width: 100%;
    .project-form-row {
      width: 100%;
      display: flex;
      column-gap: 4rem;
      margin-bottom: 2rem;
      .form-field {
        width: 100%;
        &.project-number {
          width: 30%;
        }
        &.external-id {
          width: 40%;
        }
        &.status {
          width: 15%;
        }
        label {
          font-size: 1.25rem;
          font-weight: 500;
        }
      }
    }
    .buttons-container {
      margin: 1rem;
      display: flex;
      &:has(> button:last-child:only-child) {
        justify-content: flex-end;
      }
      &:not(:has(> button:last-child:only-child)) {
        justify-content: space-between;
      }
    }
  }
</style>
